export type NavigationSection = 'main' | 'fixed';

export interface SidebarConfigLike {
  id: string;
  hidden: boolean;
  order: number;
}

export interface NavigationDefinition {
  id: string;
  section: NavigationSection;
  icon: string;
  labelKey: string;
  defaultLabel: string;
  path?: string;
  action?: 'open-apps';
  hasSubmenu?: boolean;
  defaultHidden?: boolean;
}

export interface LocalizedNavigationDefinition extends NavigationDefinition {
  label: string;
}

export interface ConfiguredNavigationDefinition extends LocalizedNavigationDefinition {
  hidden: boolean;
  order: number;
}

export interface NavigationPayloadEntry {
  id: string;
  hidden: boolean;
}

type TranslateFn = (key: string, defaultLabel?: string) => string;

export const MAIN_NAV_DEFINITIONS: NavigationDefinition[] = [
  {
    id: 'dashboard',
    section: 'main',
    path: '/dashboard',
    icon: 'dashboard',
    labelKey: 'nav.dashboard',
    defaultLabel: 'Dashboard',
  },
  {
    /**
     * The hub's house in 3D — a system page like the dashboard (owner,
     * 2026-10-31: *"also make system static 3d page"*). It used to be a page row,
     * so its address was `/layouts/<id>` and its label came from the row; the
     * row is still where its viewpoints live, but the page is the app's.
     */
    id: 'model',
    section: 'main',
    path: '/model',
    icon: 'home',
    labelKey: 'nav.home',
    defaultLabel: 'Home',
  },
  {
    id: 'security',
    section: 'main',
    path: '/security',
    icon: 'shield',
    labelKey: 'nav.security',
    defaultLabel: 'Security',
  },
  {
    id: 'climate',
    section: 'main',
    path: '/climate',
    icon: 'thermostat',
    labelKey: 'nav.climate',
    defaultLabel: 'Climate',
  },
  { id: 'lights', section: 'main', path: '/lights', icon: 'lightbulb', labelKey: 'nav.lights', defaultLabel: 'Lights' },
  { id: 'shades', section: 'main', path: '/shades', icon: 'roller_shades', labelKey: 'nav.shades', defaultLabel: 'Shades' },
  { id: 'energy', section: 'main', path: '/energy', icon: 'bolt', labelKey: 'nav.energy', defaultLabel: 'Energy' },
  {
    id: 'water',
    section: 'main',
    path: '/water',
    icon: 'water_drop',
    labelKey: 'nav.water',
    defaultLabel: 'Water',
  },
  {
    id: 'appliances',
    section: 'main',
    path: '/appliances',
    icon: 'home_iot_device',
    labelKey: 'nav.appliances',
    defaultLabel: 'Appliances',
  },
  {
    id: 'wellness',
    section: 'main',
    path: '/wellness',
    icon: 'spa',
    labelKey: 'nav.wellness',
    defaultLabel: 'Wellness',
  },
  {
    id: 'weather',
    section: 'main',
    path: '/weather',
    // `partly_cloudy_day` is in the repo's material-symbols meta, and is what
    // the demo weather station already carries (`torik-demo-weather.cjs`).
    icon: 'partly_cloudy_day',
    labelKey: 'nav.weather',
    defaultLabel: 'Weather',
  },
  {
    id: 'rooms',
    section: 'main',
    path: '/rooms',
    icon: 'meeting_room',
    labelKey: 'nav.rooms',
    defaultLabel: 'Rooms',
    hasSubmenu: true,
  },
  {
    id: 'devices',
    section: 'main',
    path: '/devices',
    icon: 'devices',
    labelKey: 'nav.devices',
    defaultLabel: 'Devices',
  },
  {
    id: 'cameras',
    section: 'main',
    path: '/cameras',
    icon: 'videocam',
    labelKey: 'nav.cameras',
    defaultLabel: 'Cameras',
  },
  {
    id: 'intercom',
    section: 'main',
    path: '/intercom',
    icon: 'door_front',
    labelKey: 'nav.intercom',
    defaultLabel: 'Intercom',
  },
  {
    id: 'maintenance',
    section: 'main',
    path: '/maintenance',
    icon: 'build',
    labelKey: 'nav.maintenance',
    defaultLabel: 'Maintenance',
  },
  { id: 'map', section: 'main', path: '/map', icon: 'map', labelKey: 'nav.map', defaultLabel: 'Map' },
  { id: 'media', section: 'main', path: '/media', icon: 'library_music', labelKey: 'nav.media', defaultLabel: 'Media' },
];

export const FIXED_NAV_DEFINITIONS: NavigationDefinition[] = [
  { id: 'apps', section: 'fixed', icon: 'apps', labelKey: 'nav.apps', defaultLabel: 'Apps', action: 'open-apps' },
  {
    id: 'settings',
    section: 'fixed',
    path: '/settings',
    icon: 'settings',
    labelKey: 'nav.settings',
    defaultLabel: 'Settings',
    hasSubmenu: true,
  },
];

export const NAVIGATION_DEFINITIONS = [...MAIN_NAV_DEFINITIONS, ...FIXED_NAV_DEFINITIONS];

export function localizeNavigationDefinitions(
  definitions: NavigationDefinition[],
  t: TranslateFn,
): LocalizedNavigationDefinition[] {
  return definitions.map((definition) => ({
    ...definition,
    label: t(definition.labelKey, definition.defaultLabel),
  }));
}

export function findNavigationDefinition(id: string): NavigationDefinition | undefined {
  return NAVIGATION_DEFINITIONS.find((definition) => definition.id === id);
}

export function applySidebarConfig(
  definitions: LocalizedNavigationDefinition[],
  configItems: SidebarConfigLike[],
): ConfiguredNavigationDefinition[] {
  const configMap = new Map(configItems.map((item) => [item.id, item]));

  return definitions
    .map((definition, index) => {
      const config = configMap.get(definition.id);
      return {
        ...definition,
        hidden: config?.hidden ?? Boolean(definition.defaultHidden),
        order: config?.order ?? index,
      };
    })
    .sort((left, right) => left.order - right.order);
}

export function prefixNavigationPath(path: string | undefined, hubId: string | undefined): string | undefined {
  if (!path) return undefined;
  return hubId ? `/h/${hubId}${path}` : path;
}

export function buildSidebarConfigPayload(groups: NavigationPayloadEntry[][]): SidebarConfigLike[] {
  let order = 0;
  const result: SidebarConfigLike[] = [];

  groups.forEach((group) => {
    group.forEach((item) => {
      result.push({
        id: item.id,
        hidden: item.hidden,
        order,
      });
      order += 1;
    });
  });

  return result;
}
